import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalController, Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';


import { tap, filter, mergeMap, take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import * as rootReducer from 'app/core/store';
import * as fromActions from 'app/core/store/actions/auth.actions';
import * as fromAuthSelectors from 'app/core/store/selectors/auth.selectors';
import { PlatformService } from 'app/core/services/platform.service';
import { getQueryParams } from 'app/core/store/selectors/router.selectors';
import { ToastService } from 'app/core/services/toast.service';
import { LoadAssetFail } from 'app/core/store/actions/delivery-assets.actions';
import { LoadDeliveryFail } from 'app/core/store/actions/delivery.actions';
import { VALIDATION_REGEXP } from 'app/shared/constants/app-service-constants';
import { build } from '../../../assets/build-number';
import { environment } from '../../../environments/environment';
import { ModalRegistrationComponent } from '../modal-registration/modal-registration.component';
import { ModalForgotPassComponent } from '../modal-forgot-pass/modal-forgot-pass.component';

@Component({
  standalone: false,
  selector: 'pptl-login',
  templateUrl: 'login.component.html',
  styleUrls: ['../auth.component.scss', 'login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginForm: FormGroup;
  isFormError = false;
  loginAttempts = 3;
  authErrResult$: Observable<{ errorMessage: string }>;
  isLoggingInLoading: boolean;
  errorBackendMsg = '';
  appVersion = '';

  private readonly passowrdErrorMsg =
  'The password or email address you entered is incorrect.' +
    "Reset your password or contact us if you're having trouble logging in.";

  constructor(
    private toasterService: ToastService,
    private platform: Platform,
    private fb: FormBuilder,
    private platformService: PlatformService,
    private store: Store<rootReducer.State>,
    public modalController: ModalController,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.subscribeFailLogin();
    this.subscribeSuccessLogin();
  }

  get isDevicePlatform(): boolean {
    return this.platformService.isCordova();
  }

  get isIosBrowser(): boolean {
    return this.platformService.isIosBrowser();
  }

  get isBrowser(): boolean {
    return this.platformService.isBrowser();
  }

  get isMobile(): boolean {
    return this.platformService.isMobile();
  }

  get isBuildDev(): boolean {
    return environment.pptlApi.includes('dev');
  }

  get isBuildStage(): boolean {
    return environment.pptlApi.includes('uat');
  }

  get getBrowserClass() {
    return {
      'ios-browser': this.isIosBrowser,
    };
  }

  ngOnInit() {
    this.initLoginForm();
    this.stopDeliveryLoader();
    this.appVersion = build.version;
  }

  ngAfterViewInit(): void {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    SplashScreen.show();
    setTimeout(() => {
      SplashScreen.hide();
    }, 3000);
  }

  initLoginForm(): void {
    this.loginForm = this.fb.group({
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(VALIDATION_REGEXP.SIMPLE_EMAIL),
        ]),
      ],
      password: ['', Validators.compose([Validators.required, Validators.minLength(8)])],
    });
  }

  async showForgotPassModal() {
    this.resetAuthState();
    const modalForgotPass = await this.modalController.create({
      component: ModalForgotPassComponent,
      cssClass: 'modal-forgot-pass',
      componentProps: {},
      backdropDismiss: false,
    });

    await modalForgotPass.present();
  }

  async showRegistrationModal() {
    this.resetAuthState();
    const modalRegistration = await this.modalController.create({
      component: ModalRegistrationComponent,
      cssClass: 'modal-registration',
      componentProps: {},
      backdropDismiss: false,
    });

    await modalRegistration.present();
  }

  showAccountLockoutToast() {
    this.toasterService.showToast(this.passowrdErrorMsg);
  }

  onSubmit() {
    if (!this.loginForm.invalid) {
      this.isLoggingInLoading = true;
      this.store.dispatch(fromActions.AuthLogin({ payload: this.loginForm.value }));
    } else {
      this.isFormError = true;
    }
  }

  subscribeSuccessLogin() {
    this.store
      .pipe(
        select(fromAuthSelectors.getAuthAccessToken),
        filter((isLoginned: any) => !!isLoginned),
        mergeMap(() => this.store.pipe(select(getQueryParams))),
        take(1),
      )
      .subscribe((queryParams: { backUrl: string }) => {
        this.isLoggingInLoading = false;
        let url = queryParams?.backUrl || '';
        if (url === '/favorite') {
          url = '/inbox';
        }
        this.router.navigateByUrl(url);
      });
  }

  subscribeFailLogin() {
    this.authErrResult$ = this.store.pipe(
      select(fromAuthSelectors.getAuthError),
      tap((resp: any) => {
        this.isLoggingInLoading = false;
        this.isFormError = !!resp.errorMessage;

        if (!resp?.loginAttemptsData?.failedCount && resp?.loginAttemptsData?.message) {
          this.errorBackendMsg = resp?.loginAttemptsData?.message;
        } else {
          this.errorBackendMsg = this.isFormError ? resp?.errorMessage : '';
        }
        this.changeDetectorRef.detectChanges();
      }),
    );
  }

  onKeydown(event: any) {
    this.isFormError = false;
    if (event.keyCode === 13 && !this.loginForm?.invalid) {
      this.onSubmit();
    }
  }

  onForgotPassClick() {
    this.showForgotPassModal();
  }

  onRegistrationClick() {
    this.showRegistrationModal();
  }

  private resetAuthState() {
    this.store.dispatch(fromActions.ResetErrors());
    this.store.dispatch(fromActions.ResetLoaded());
  }

  private stopDeliveryLoader() {
    this.store.dispatch(LoadDeliveryFail());
    this.store.dispatch(LoadAssetFail({ payload: '' }));
  }
}
